// src/Sidebar.jsx
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { SidebarStyles, AddLocationModal, SurveyModal } from "./SidebarComponents"; // <-- Imported components

// Prefer env var; fallback to your server
const API_BASE = (
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) || 
  process.env.REACT_APP_API_BASE || 
  "/api" 
).replace(/\/+$/, "");

export default function Sidebar() {
  const navigate = useNavigate();

  const [user, setUser] = useState({
    displayName: "User",
    displayEmail: "",
    profileImg: "",
    initials: "U",
  });
  const [loading, setLoading] = useState(false);

  // Organization the current user belongs to
  const [organization, setOrganization] = useState(() => localStorage.getItem("organization") || "");

  // Locations UI state
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [locations, setLocations] = useState(null); 
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [openLocationId, setOpenLocationId] = useState(null);
  const [surveyRemark, setSurveyRemark] = useState("");

  // Surveys state
  const [surveys, setSurveys] = useState(null); 
  const [surveysLoading, setSurveysLoading] = useState(false);

  // Modal state for adding location
  const [showAddModal, setShowAddModal] = useState(false);
  const [addName, setAddName] = useState("");
  const [addUploading, setAddUploading] = useState(false);
  const [addError, setAddError] = useState("");
  const fileRef = useRef(null);
  const [addFileName, setAddFileName] = useState("");

  // Modal state for requesting survey
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [surveyTarget, setSurveyTarget] = useState(null);
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [surveyStatus, setSurveyStatus] = useState("");
  const [surveyDate, setSurveyDate] = useState("");

  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyLoadedOnce, setSurveyLoadedOnce] = useState(false);
  const [surveyDefaults, setSurveyDefaults] = useState(null);

  // --- Helpers ---
  const readUserFromStorage = () => {
    try {
      const name = (localStorage.getItem("name") || "").trim();
      const email = (localStorage.getItem("email") || "").trim();
      const profileImage = localStorage.getItem("profileImage") || "";
      const displayName = name || "User";
      setUser({
        displayName,
        displayEmail: email,
        profileImg: profileImage,
        initials: displayName && displayName[0] ? displayName[0].toUpperCase() : "U",
      });
      const org = (localStorage.getItem("organization") || "").trim();
      setOrganization(org);
    } catch (err) {
      console.error("readUserFromStorage:", err);
    }
  };

  const normalizeSurveyTime = (raw) => {
    if (raw == null) return raw;
    if (typeof raw === "object" && (raw.seconds !== undefined || raw._seconds !== undefined)) {
      const secs = raw.seconds ?? raw._seconds;
      const date = new Date(Number(secs) * 1000);
      if (!isNaN(date.getTime())) return date.toISOString().split("T")[0];
      return raw;
    }
    return raw;
  };

  const toDateKey = (raw) => {
    if (raw == null) return null;
    if (typeof raw === "object" && (raw.seconds !== undefined || raw._seconds !== undefined)) {
      const secs = raw.seconds ?? raw._seconds;
      const d = new Date(Number(secs) * 1000);
      if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
      return null;
    }
    if (raw instanceof Date && !isNaN(raw.getTime())) return raw.toISOString().split("T")[0];
    if (typeof raw === "number" && Number.isFinite(raw)) {
      const ms = raw < 1e12 ? raw * 1000 : raw;
      const d = new Date(ms);
      if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
      return null;
    }
    if (typeof raw === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
      const d = new Date(raw);
      if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
      return null;
    }
    return null;
  };

  const parseDateForSort = (val) => {
    if (val == null) return 0;
    if (typeof val === "object" && (val.seconds !== undefined || val._seconds !== undefined)) {
      const secs = val.seconds ?? val._seconds;
      const t = Number(secs) * 1000;
      return Number.isFinite(t) ? t : 0;
    }
    if (typeof val === "number" && Number.isFinite(val)) {
      return val < 1e12 ? val * 1000 : val;
    }
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const d = new Date(val + "T00:00:00Z");
      return isNaN(d.getTime()) ? 0 : d.getTime();
    }
    const p = Date.parse(val);
    return isNaN(p) ? 0 : p;
  };

  const pickRowTimestamp = (r) =>
    Math.max(
      parseDateForSort(r.progress_time),
      parseDateForSort(r.progressTime),
      parseDateForSort(r.updated_at),
      parseDateForSort(r.modified_at),
      parseDateForSort(r.updatedAt),
      parseDateForSort(r.modifiedAt),
      parseDateForSort(r.timestamp),
      parseDateForSort(r.created_at),
      parseDateForSort(r.createdAt),
      parseDateForSort(r.survey_time),
      0
    );

  const normalizeNameKey = (val) => (String(val ?? "").trim().toLowerCase());

  // --- Fetch Logic ---
  const fetchProfileAndStore = async (signal) => {
    const token = localStorage.getItem("token");
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/me`, {
        method: "GET",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        signal,
      });
      if (res.status === 401) {
        handleSignOut();
        return;
      }
      if (!res.ok) return;
      const data = await res.json();
      if (!data) return;

      if (data.name) localStorage.setItem("name", data.name);
      if (data.email) localStorage.setItem("email", data.email);
      if (data.organization) localStorage.setItem("organization", data.organization);
      if (data.profileImage) localStorage.setItem("profileImage", data.profileImage);

      readUserFromStorage();
      window.dispatchEvent(new Event("user-updated"));
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    ["token", "name", "email", "profileImage", "organization", "rememberEmail"].forEach((k) =>
      localStorage.removeItem(k)
    );
    window.dispatchEvent(new Event("user-updated"));
    navigate("/");
  };

  // --- Effects ---
  useEffect(() => {
    readUserFromStorage();
    const token = localStorage.getItem("token");
    const hasName = !!localStorage.getItem("name");
    if (token && !hasName) {
      const ctrl = new AbortController();
      fetchProfileAndStore(ctrl.signal);
      return () => ctrl.abort();
    }
    return undefined;
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (!e) return;
      if (["token", "name", "email", "profileImage", "organization"].includes(e.key)) {
        readUserFromStorage();
        if (e.key === "organization") {
          setLocations(null);
          setSurveys(null);
        }
        const token = localStorage.getItem("token");
        const hasName = !!localStorage.getItem("name");
        if (token && !hasName) {
          const ctrl = new AbortController();
          fetchProfileAndStore(ctrl.signal);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    const onUserUpdated = () => {
      readUserFromStorage();
      setLocations(null);
      setSurveys(null);
    };
    window.addEventListener("user-updated", onUserUpdated);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("user-updated", onUserUpdated);
    };
  }, []);

  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      const hasName = !!localStorage.getItem("name");
      if (token && !hasName) {
        try {
          const decoded = jwtDecode(token);
          const name = decoded?.name || decoded?.username || "";
          const email = decoded?.email || "";
          if (name) localStorage.setItem("name", name);
          if (email) localStorage.setItem("email", email);
          if (name || email) {
            readUserFromStorage();
            const ctrl = new AbortController();
            fetchProfileAndStore(ctrl.signal);
            return () => ctrl.abort();
          }
        } catch (err) {
          const ctrl = new AbortController();
          fetchProfileAndStore(ctrl.signal);
          return () => ctrl.abort();
        }
      }
    } catch (err) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLocations = async (signal) => {
    if (locations !== null) return;
    setLocationsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Accept: "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;

      const org = (organization || "").trim();
      let url = `${API_BASE}/project_mapx`;
      if (org) url += `?organization=${encodeURIComponent(org)}`;

      const res = await fetch(url, { method: "GET", headers, signal });
      if (!res.ok) {
        setLocations([]);
        return;
      }
      const data = await res.json();
      let filtered = [];
      if (Array.isArray(data)) {
        filtered = org ? data.filter((d) => String(d.organization || "").trim() === org) : data;
      } else if (data && Array.isArray(data.items)) {
        filtered = org ? data.items.filter((d) => String(d.organization || "").trim() === org) : data.items;
      }
      setLocations(filtered);
    } catch (err) {
      if (err.name === "AbortError") return;
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  const loadSurveys = async (signal) => {
    if (surveys !== null) return;
    setSurveysLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Accept: "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;
      const org = (organization || "").trim();
      let url = `${API_BASE}/survey_request`;
      if (org) url += `?organization=${encodeURIComponent(org)}`;

      const res = await fetch(url, { method: "GET", headers, signal });
      if (!res.ok) {
        setSurveys([]);
        return;
      }
      const data = await res.json();
      setSurveys(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name === "AbortError") return;
      setSurveys([]);
    } finally {
      setSurveysLoading(false);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    setLocations(null);
    setSurveys(null);
    loadLocations(ctrl.signal);
    loadSurveys(ctrl.signal);
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const toggleLocations = () => {
    const willOpen = !locationsOpen;
    setLocationsOpen(willOpen);
    if (willOpen && locations === null) {
      const ctrl = new AbortController();
      loadLocations(ctrl.signal);
    }
  };

  // --- Modal Handlers ---
  const handleAddLocation = () => {
    setAddName("");
    setAddError("");
    setAddFileName("");
    if (fileRef.current) fileRef.current.value = "";
    setShowAddModal(true);
  };
  const closeAddModal = () => setShowAddModal(false);

  useEffect(() => {
    document.body.style.overflow = showAddModal || surveyModalOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showAddModal, surveyModalOpen]);

  const submitAddLocation = async (e) => {
    e?.preventDefault?.();
    setAddError("");
    const name = (addName || "").trim();
    if (!name) { setAddError("Please enter a location name."); return; }
    const token = localStorage.getItem("token");
    const form = new FormData();
    const orgToSend = localStorage.getItem("organization") || "";
    const email = localStorage.getItem("email") || localStorage.getItem("rememberEmail") || "";
    form.append("organization", orgToSend);
    form.append("location_name", name);
    form.append("email", email);
    const file = fileRef.current?.files?.[0];
    if (file) form.append("geojson_file", file);

    setAddUploading(true);
    try {
      const res = await fetch(`${API_BASE}/project_mapx`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      if (res.status === 401) { handleSignOut(); return; }
      const result = await res.json().catch(() => null);
      if (!res.ok) { setAddError(result?.message || `Failed to add: HTTP ${res.status}`); return; }
      
      const inserted = result?.project || result;
      closeAddModal();
      if (inserted) {
        setLocations((prev) => {
          const arr = Array.isArray(prev) ? prev.slice() : [];
          arr.unshift(inserted);
          return arr;
        });
        setLocationsOpen(true);
        setOpenLocationId(inserted.id ?? inserted._id ?? inserted.location_name ?? inserted.name);
      } else {
        setLocations(null);
      }
    } catch (err) {
      setAddError("Network error.");
    } finally {
      setAddUploading(false);
    }
  };

  // --- Merging & Sorting Logic ---
  const mergeSurveysIntoState = (incoming) => {
    if (!incoming) return;
    const incomingRows = Array.isArray(incoming) ? incoming : [incoming];

    setSurveys((prev) => {
      const existing = Array.isArray(prev) ? prev.slice() : [];
      const findIndexFor = (row) => {
        if (row.id != null) {
          const idx = existing.findIndex((e) => e.id === row.id || e._id === row.id);
          if (idx !== -1) return idx;
        }
        const locKey = normalizeNameKey(row.location_name ?? row.location ?? "");
        const surveyKey = toDateKey(row.survey_time ?? row.survey_date ?? row.surveyTime ?? null);
        return existing.findIndex((e) => {
          const eLoc = normalizeNameKey(e.location_name ?? e.location ?? "");
          const eSurvey = toDateKey(e.survey_time ?? e.survey_date ?? e.surveyTime ?? null);
          return eLoc === locKey && (eSurvey ?? null) === (surveyKey ?? null);
        });
      };

      for (const incRaw of incomingRows) {
        const inc = { ...(incRaw || {}) };
        const nk = toDateKey(inc.survey_time ?? inc.survey_date ?? inc.surveyTime ?? null);
        if (nk) inc.survey_time = nk;
        const incFresh = pickRowTimestamp(inc);
        const idx = findIndexFor(inc);
        if (idx === -1) {
          existing.unshift({ ...inc, _freshness: incFresh });
          continue;
        }
        const curr = existing[idx];
        const currFresh = Number.isFinite(curr._freshness) ? curr._freshness : pickRowTimestamp(curr);
        if (incFresh > currFresh) {
          existing[idx] = { ...curr, ...inc, _freshness: incFresh };
        } else if (incFresh === currFresh) {
          const safeMerge = { ...curr, _freshness: currFresh };
          for (const [k, v] of Object.entries(inc)) {
            if (v != null && safeMerge[k] == null) safeMerge[k] = v;
          }
          existing[idx] = safeMerge;
        }
      }
      existing.sort((a, b) => {
        const aDate = toDateKey(a.survey_time) || "";
        const bDate = toDateKey(b.survey_time) || "";
        if (aDate === bDate) return (b._freshness || pickRowTimestamp(b)) - (a._freshness || pickRowTimestamp(a));
        return bDate.localeCompare(aDate);
      });
      return existing;
    });
  };

  useEffect(() => {
    let es;
    let pollInterval = null;
    let isSseAvailable = typeof window !== "undefined" && "EventSource" in window;
    const org = (organization || "").trim();
    const streamUrl = `${API_BASE}/survey_request/stream${org ? `?organization=${encodeURIComponent(org)}` : ""}`;

    const startPolling = () => {
      if (pollInterval) return;
      pollInterval = setInterval(() => {
        (async () => {
            try {
                const token = localStorage.getItem("token");
                const headers = { Accept: "application/json" };
                if (token) headers.Authorization = `Bearer ${token}`;
                let url = `${API_BASE}/survey_request`;
                if (org) url += `?organization=${encodeURIComponent(org)}&_ts=${Date.now()}`;
                const res = await fetch(url, { method: "GET", headers });
                if (!res.ok) return;
                const data = await res.json();
                if (!data) return;
                mergeSurveysIntoState(Array.isArray(data) ? data : data.survey ? data.survey : data);
            } catch (err) {}
        })();
      }, 10000);
    };

    if (isSseAvailable) {
      try {
        es = new EventSource(streamUrl);
        es.onmessage = (ev) => {
          if (!ev || !ev.data) return;
          try {
            const payload = JSON.parse(ev.data);
            const rows = payload?.data ?? payload;
            mergeSurveysIntoState(rows);
          } catch (err) {}
        };
        es.onerror = () => {
           if(es) es.close();
           es = null;
           startPolling();
        }
      } catch (err) { startPolling(); }
    } else {
      startPolling();
    }
    return () => {
      if (es) es.close();
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [organization]); // eslint-disable-line

  const toggleLocationItem = (id) => setOpenLocationId((prev) => (prev === id ? null : id));

  // --- Survey Request Modal ---
  const requestSurvey = (loc) => {
    const id = loc.id ?? loc._id ?? loc.location_name ?? loc.name;
    const title = loc.location_name ?? loc.name ?? loc.title ?? "Untitled location";
    setSurveyTarget({ id, title, raw: loc });
    setSurveyStatus("");
    setSurveyDate("");
    setSurveyRemark("");
    setSurveyModalOpen(true);
  };
  const closeSurveyModal = () => {
    setSurveyModalOpen(false);
    setSurveyTarget(null);
    setSurveySubmitting(false);
  };

  // Populate survey defaults
  useEffect(() => {
    if (!surveyModalOpen || !surveyTarget) return;
    if (surveyLoadedOnce && surveyDefaults?.id === surveyTarget.id) { return; }
    let cancelled = false;
    const load = async () => {
        setSurveyLoading(true);
        const minDelay = new Promise(res => setTimeout(res, 300));
        const getPayload = () => {
            try {
                const list = surveysForLocation(surveyTarget.raw ?? surveyTarget);
                return Array.isArray(list) && list.length > 0 ? list[0] : null;
            } catch (e) { return null; }
        };
        try {
            const [payload] = await Promise.all([getPayload(), minDelay]);
            if (cancelled) return;
            setSurveyDefaults(payload);
            if (payload?.survey_time) setSurveyDate(normalizeSurveyTime(payload.survey_time));
            if (payload?.remark) setSurveyRemark(payload.remark);
            setSurveyLoadedOnce(true);
        } catch (err) {
        } finally {
            if (!cancelled) setSurveyLoading(false);
        }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyModalOpen, surveyTarget]);

  const confirmRequestSurvey = async () => {
    if (!surveyTarget) return;
    setSurveySubmitting(true);
    setSurveyStatus("");
    try {
        const token = localStorage.getItem("token");
        const payload = {
            location_name: surveyTarget.raw.location_name ?? surveyTarget.raw.name ?? surveyTarget.title,
            survey_time: surveyDate || null,
            organization: localStorage.getItem("organization") || null,
            email: localStorage.getItem("email") || null,
            status: "Requested",
            geojson_file: surveyTarget.raw.geojson_file || surveyTarget.raw.boundary || null,
            remark: (surveyRemark || "").trim() || null,
        };
        const res = await fetch(`${API_BASE}/survey_request`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify(payload),
        });
        if (res.status === 401) { handleSignOut(); return; }
        if (!res.ok) throw new Error();
        const data = await res.json();
        const inserted = data?.survey || data;
        
        closeSurveyModal();
        setSurveyStatus(`Survey requested for “${surveyTarget.title}”`);
        if (inserted) {
            if (inserted.remark == null && payload.remark) inserted.remark = payload.remark;
            setSurveys(prev => [inserted, ...(prev || [])]);
            window.dispatchEvent(new CustomEvent("survey-request-created", { detail: inserted }));
        } else {
            setSurveys(null);
            loadSurveys(new AbortController().signal);
        }
        setTimeout(() => setSurveyStatus(""), 4500);
    } catch (err) {
        setSurveyStatus("Failed to request survey.");
        setSurveySubmitting(false);
    }
  };

  const deleteSurvey = async (survey) => {
    if (!survey || !window.confirm("Delete this survey entry?")) return;
    try {
        const token = localStorage.getItem("token");
        const id = survey.id ?? survey._id;
        const res = await fetch(`${API_BASE}/survey_request/${encodeURIComponent(id)}`, {
            method: "DELETE",
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (res.status === 401) { handleSignOut(); return; }
        if (res.ok) {
            setSurveys(prev => prev.filter(r => (r.id ?? r._id) !== id));
        }
    } catch(err) { alert("Failed delete"); }
  };

  const deleteLocation = async (loc) => {
    const id = loc.id ?? loc._id ?? loc.location_name ?? loc.name;
    if (!window.confirm(`Delete location?`)) return;
    try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/project_mapx/${encodeURIComponent(id)}`, {
            method: "DELETE",
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (res.status === 401) { handleSignOut(); return; }
        if (res.ok) {
            setLocations(prev => prev.filter(p => (p.id ?? p._id ?? p.location_name ?? p.name) !== id));
            setOpenLocationId(prev => prev === id ? null : prev);
        }
    } catch(err) { alert("Failed delete"); }
  };

  const findLatestForLocation = (locName) => {
    if (!Array.isArray(surveys) || !locName) return null;
    const key = normalizeNameKey(locName);
    const extractLatestTimestamp = (row) => {
        const fields = [row.survey_time, row.updated_at, row.modified_at, row.timestamp, row.created_at];
        const parsed = fields.map(v => parseDateForSort(v)).filter(v => v > 0);
        return parsed.length > 0 ? Math.max(...parsed) : 0;
    };
    const candidates = surveys
        .filter(s => normalizeNameKey(s.location_name ?? s.location ?? "") === key)
        .map(s => ({ s, t: extractLatestTimestamp(s) }))
        .filter(item => item.t > 0);
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => b.t - a.t);
    return candidates[0].s;
  };

  const surveysForLocation = (location) => {
    if (!Array.isArray(surveys)) return [];
    const locKey = normalizeNameKey(location?.location_name ?? location?.name ?? "");
    if (!locKey) return [];
    
    const allForLoc = surveys.filter(s => normalizeNameKey(s.location_name ?? s.location ?? "") === locKey);
    const groups = {};
    for (const s of allForLoc) {
        const dateKey = toDateKey(s.survey_time ?? s.survey_date ?? s.surveyTime ?? null) || "unknown";
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(s);
    }
    const statusOrder = ["requested", "surveyed", "processed", "uploaded", "verified"];
    const statusMap = Object.fromEntries(statusOrder.map((s, i) => [s, i]));

    const merged = Object.keys(groups).map(dateKey => {
        const items = groups[dateKey];
        let bestRow = null, bestIdx = -1, bestTs = 0, groupMaxTs = 0;
        for (const row of items) {
            const currentStatus = String(row.status ?? "").trim().toLowerCase();
            const idx = statusMap[currentStatus] ?? -1;
            const ts = pickRowTimestamp(row);
            groupMaxTs = Math.max(groupMaxTs, ts);
            if (idx > bestIdx) { bestIdx = idx; bestRow = row; bestTs = ts; }
            else if (idx === bestIdx && ts > bestTs) { bestRow = row; bestTs = ts; }
        }
        if(!bestRow) bestRow = items[0];
        
        const displayTimestamp = bestRow.progress_time ?? bestRow.updated_at ?? bestRow.timestamp ?? bestRow.survey_time ?? null;
        return {
            ...bestRow,
            survey_time: dateKey,
            latest_status: statusOrder[bestIdx] ?? String(bestRow.status ?? "unknown"),
            latest_modified_ts: groupMaxTs,
            latest_modified_value: displayTimestamp
        };
    }).filter(Boolean);

    merged.sort((a, b) => {
        if(a.survey_time === "unknown") return 1;
        if(b.survey_time === "unknown") return -1;
        if(a.survey_time === b.survey_time) return b.latest_modified_ts - a.latest_modified_ts;
        return b.survey_time.localeCompare(a.survey_time);
    });
    return merged;
  };

  const formatSurveyTime = (raw) => {
    if (!raw && raw !== 0) return "";
    try {
        const d = new Date(raw);
        if(!isNaN(d.getTime())) {
            return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}`;
        }
    } catch(e) {}
    return String(raw);
  };

  useEffect(() => {
    const onSurveyCreated = (ev) => {
        const payload = ev?.detail ?? ev;
        if(payload) mergeSurveysIntoState(Array.isArray(payload) ? payload : (payload.data ? payload.data : [payload]));
    };
    window.addEventListener("survey-request-created", onSurveyCreated);
    window.addEventListener("survey-request-updated", onSurveyCreated);
    return () => {
        window.removeEventListener("survey-request-created", onSurveyCreated);
        window.removeEventListener("survey-request-updated", onSurveyCreated);
    };
  }, []);

  const { displayName, displayEmail, profileImg, initials } = user;

  // --- Render ---
  return (
    <aside className="sidebar" aria-hidden={loading ? "true" : "false"}>
      {/* Styles are now imported */}
      <SidebarStyles />

      <div className="brand">
        <div className="logo"><i className="bi bi-map-fill" /></div>
        <h1 className="title">Terra MapX</h1>
      </div>

      <ul className="menu-list">
        <li>
          <NavLink
            to="/Homepage"
            className="menu-link"
            onClick={() => {
                try {
                    const clearPayload = { clear: true, from: "homepage" };
                    sessionStorage.setItem("survey-selected-payload", JSON.stringify(clearPayload));
                    window.dispatchEvent(new CustomEvent("survey-selected", { detail: clearPayload }));
                } catch (e) {}
            }}
          >
            <i className="bi bi-house-fill" /> <span>Homepage</span>
          </NavLink>
        </li>

        <li>
          <div
            role="button" tabIndex={0}
            className={`menu-link ${locationsOpen ? "active" : ""}`}
            onClick={toggleLocations}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleLocations()}
          >
            <i className="bi bi-map" /> <span>Locations & Surveys</span>
            <div style={{ marginLeft: "auto", opacity: 0.9 }}>
              <i className={`bi ${locationsOpen ? "bi-chevron-up" : "bi-chevron-down"}`} />
            </div>
          </div>

          {locationsOpen && (
            <div id="locations-panel" className="locations-panel">
              {locationsLoading && <div className="loc-loading">Loading locations…</div>}
              {!locationsLoading && locations?.length === 0 && (
                <>
                  <div className="locations-empty">No locations yet</div>
                  <div className="add-location" onClick={handleAddLocation} role="button" tabIndex={0}>
                    <i className="bi bi-plus-lg" /> <span>Add new location</span>
                  </div>
                </>
              )}
              {!locationsLoading && locations?.length > 0 && (
                <>
                  <div className="locations-list">
                    {locations.map((loc) => {
                      const id = loc.id ?? loc._id ?? loc.location_name ?? loc.name;
                      const title = loc.location_name ?? loc.name ?? loc.title ?? "Untitled";
                      const locSurveys = surveysForLocation(loc);
                      const idKey = String(id ?? title);

                      return (
                        <div key={idKey} className="loc-item">
                          <div className="loc-item-header">
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              <i className="bi bi-geo-alt" />
                              <div
                                className="loc-item-title"
                                title="Open location in map"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const target = loc.project_name ?? loc.projectName ?? loc.location_name ?? loc.name ?? id;
                                    const routeKey = String(target ?? "").trim();
                                    if(!routeKey) return;
                                    try { localStorage.setItem("location_name", routeKey); } catch(e){}
                                    const clearPayload = { clear: true, location_name: routeKey, location: loc };
                                    try { sessionStorage.setItem("survey-selected-payload", JSON.stringify(clearPayload)); } catch(e){}
                                    try { window.dispatchEvent(new CustomEvent("survey-selected", { detail: clearPayload })); } catch(e){}
                                    navigate(`/map/${encodeURIComponent(routeKey)}?_t=${Date.now()}`);
                                    setTimeout(() => {
                                        try { window.dispatchEvent(new CustomEvent("survey-selected", { detail: clearPayload })); } catch (_) {}
                                    }, 60);
                                }}
                                style={{ cursor: "pointer", fontWeight: 600 }}
                              >
                                {title}
                              </div>
                            </div>
                            <div onClick={(e) => { e.stopPropagation(); toggleLocationItem(idKey); }} style={{ padding: 6, cursor: "pointer", color: "var(--nav-muted)" }}>
                                <i className={`bi ${openLocationId === idKey ? "bi-dash-lg" : "bi-plus-lg"}`} />
                            </div>
                          </div>

                          {openLocationId === idKey && (
                            <div className="loc-item-body" onClick={(e) => e.stopPropagation()} style={{ padding: "8px" }}>
                              <div style={{ marginBottom: 12 }}>
                                <button type="button" onClick={(e) => { e.stopPropagation(); deleteLocation(loc); }} style={{ width: "100%", padding: "10px", borderRadius: 6, border: "1px solid rgba(255,0,0,0.12)", background: "rgba(233, 203, 203, 0.12)", color: "#ef4444", cursor: "pointer", fontWeight: 700, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
                                  <i className="bi bi-trash" /> <span>Delete Location</span>
                                </button>
                              </div>
                              {locSurveys.map(s => {
                                  const displayModifiedRaw = s.latest_modified_value;
                                  const displayStatusRaw = String(s.latest_status ?? "requested").trim().toLowerCase();
                                  const statusClass = `status-${displayStatusRaw.replace(/\s+/g, "-")}`;
                                  const itemKey = s.id ?? `${s.location_name}::${s.survey_time}`;
                                  
                                  return (
                                    <div key={itemKey} className="survey-entry">
                                        <div className="survey-left">
                                            <span className={`status-dot ${statusClass}`} title={displayStatusRaw} />
                                            <div className="survey-time-text"
                                                style={{cursor:"pointer"}}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const locNameKey = normalizeNameKey(s.location_name ?? loc.location_name ?? "");
                                                    const history = Array.isArray(surveys) ? surveys.filter(r => normalizeNameKey(r.location_name ?? r.location ?? "") === locNameKey) : [];
                                                    const payload = { ...s, survey_time: normalizeSurveyTime(s.survey_time), location_name: loc.location_name ?? loc.name, history };
                                                    try { sessionStorage.setItem("survey-selected-payload", JSON.stringify(payload)); } catch(e){}
                                                    try { window.dispatchEvent(new CustomEvent("survey-selected", { detail: payload })); } catch(e){}
                                                    navigate(`/map/${encodeURIComponent(payload.location_name)}?_s=${s.id ?? Date.now()}`);
                                                    setTimeout(() => { try { window.dispatchEvent(new CustomEvent("survey-selected", { detail: payload })); } catch(e){} }, 60);
                                                }}
                                            >
                                                {s.survey_time === "unknown" ? "(no date)" : formatSurveyTime(s.survey_time)}
                                                <div className="survey-modified">{displayModifiedRaw ? `Updated: ${formatSurveyTime(displayModifiedRaw)}` : ""}</div>
                                            </div>
                                        </div>
                                        <div className="survey-actions">
                                            <button onClick={(e) => { e.stopPropagation(); deleteSurvey(s); }}><i className="bi bi-trash" /></button>
                                        </div>
                                    </div>
                                  );
                              })}
                              <div style={{ marginTop: 12 }}>
                                <button type="button" onClick={(e) => { e.stopPropagation(); requestSurvey(loc); }} style={{ width: "100%", padding: "10px", borderRadius: 6, border: "none", background: "#10b981", color: "#fff", fontWeight: 700, cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
                                    <i className="bi bi-plus-lg" /> <span>Request New Survey</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="add-location" onClick={handleAddLocation} role="button" tabIndex={0}>
                    <i className="bi bi-plus-lg" /> <span>Add new location</span>
                  </div>
                </>
              )}
            </div>
          )}
        </li>
      </ul>

      <div className="sidebar-footer">
        <div className="user-tile" onClick={() => navigate("/profile")}>
          <div className="user-avatar">{profileImg ? <img src={profileImg} alt="Profile" /> : initials}</div>
          <div className="user-meta">
            <div className="name">{displayName}</div>
            <div className="email">{displayEmail}</div>
          </div>
        </div>
        <div className="user-actions">
          <button type="button" onClick={handleSignOut} className="action-link text-danger"><i className="bi bi-box-arrow-right" /> <span>Sign Out</span></button>
        </div>
        {surveyStatus && <div style={{ marginTop: 10, fontSize: 13, color: "#10b981", fontWeight: 700 }}>{surveyStatus}</div>}
      </div>

      {/* RENDER MODALS IMPORTED FROM SIDEBARCOMPONENTS */}
      <AddLocationModal 
        show={showAddModal} 
        onClose={closeAddModal} 
        onSubmit={submitAddLocation}
        name={addName}
        setName={setAddName}
        fileRef={fileRef}
        fileName={addFileName}
        setFileName={setAddFileName}
        error={addError}
        uploading={addUploading}
      />

      <SurveyModal 
        show={surveyModalOpen}
        target={surveyTarget}
        onClose={closeSurveyModal}
        onConfirm={confirmRequestSurvey}
        date={surveyDate}
        setDate={setSurveyDate}
        remark={surveyRemark}
        setRemark={setSurveyRemark}
        loading={surveyLoading}
        submitting={surveySubmitting}
        status={surveyStatus}
      />
    </aside>
  );
}